import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LoadingElement from "../loading-element/loading-element";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartPlus,
  faCheck,
  faNoteSticky,
} from "@fortawesome/free-solid-svg-icons";
import { Dialog_VM, DialogFrame, useDialog } from "./dialog-base";
import { ApprovalDialog } from "./useApprovalDialogByRole";
import { InputWrapper } from "../Input/InputWrapper";
import { CustomInput } from "../Input/CustomInput";
import { InputError } from "../../screens/my-profile/InputError";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { Organization } from "../../../core/models/entities/Organization";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Profile } from "../../../core/models/entities/Profile";
import { useCartHidden } from "../../../hooks/useCartHidden";
import { enviroment } from "../../../enviroment";

const getCustomerFromEntity = (
  entity: any,
  profile?: Profile
): Organization | undefined => {
  const customer = entity?.customer as Organization;
  if (
    !(customer && profile) ||
    profile?.userOrganizationInformations.organizationId === customer.id
  )
    return undefined;
  return customer;
};

interface Props<T> {
  subject: string;
  onSkip: (entity: T) => Promise<void>;
  onOrder: (entity: T) => Promise<void>;
  onInitApproval: (entity: T, email?: string) => Promise<void>;
  // Flag editorNoCustomerSkipEnabled: see useApprovalDialogByRole.
  hasNoCustomer?: (entity: T) => boolean;
  // B3(b) (flag logoRejectAndEditBlockCustomerSendEnabled, carried by the
  // rule itself): true for an entity whose "Send Approval to Customer" must
  // be disabled - a NAV logo the dealer is fixing themselves, until the
  // dealer has approved the fix. Omitted = today's dialog.
  isCustomerSendBlocked?: (entity: T) => boolean;
}

export function useApprovalDialogVendor<T>({
  subject,
  onSkip,
  onOrder: onSkipAndOrder,
  onInitApproval,
  hasNoCustomer,
  isCustomerSendBlocked,
}: Props<T>): ApprovalDialog<T> {
  const initApproval = useDialog();
  const emailDialog = useDialog();
  const { profile } = useProfileContext();
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState<T>();
  // Counts start(entity) calls; the key of the e-mail dialog for a design
  // without a customer (see noCustomer below). Unused with the flag off.
  const [openCount, setOpenCount] = useState(0);

  function handleApproveWithExternal(email?: string) {
    setLoading(true);
    onInitApproval(entity!, email).finally(() => {
      setLoading(false);
      emailDialog.closeDialog();
    });
  }

  function cancel() {
    emailDialog.closeDialog();
    initApproval.openDialog();
  }

  function handleSkip() {
    setLoading(true);
    onSkip(entity!).finally(() => {
      setLoading(false);
      initApproval.closeDialog();
    });
  }

  function handleStartApproval() {
    initApproval.closeDialog();
    emailDialog.openDialog();
  }

  function handleSkipAndOrder() {
    setLoading(true);
    onSkipAndOrder(entity!).finally(() => {
      setLoading(false);
      initApproval.closeDialog();
    });
  }

  const email = getCustomerFromEntity(entity, profile)?.email;
  // Flag editorNoCustomerSkipEnabled: a design without a customer gets a
  // third choice, "Approve without ordering" (= onSkip, the dealer's finish
  // IS the approval), as a button beside "Send Approval to Customer" (kept:
  // the dealer types the address the approval goes to) and "Add to order",
  // plus a line explaining the choice; the italic link gives way to the
  // button. Without the skip button a design sent to nobody hangs in
  // approval with only Delete.
  // Flag off, no callback (logos) or no entity yet = false = today's dialog.
  const noCustomer =
    enviroment.editorNoCustomerSkipEnabled &&
    entity !== undefined &&
    (hasNoCustomer?.(entity) ?? false);
  // B3(b): the send button is blocked for THIS entity (judged on every
  // start(entity) - the rule reads the entity the dialog was started for).
  // No rule (the list, the design callers) or no entity yet = false.
  const customerSendBlocked =
    entity !== undefined && (isCustomerSendBlocked?.(entity) ?? false);
  return {
    component: (
      <>
        <InitiateApprovalDialog2
          loading={loading}
          onSkip={handleSkip}
          onInitiateApproval={handleStartApproval}
          onSkipAndOrder={handleSkipAndOrder}
          subject={subject}
          dialogVm={initApproval}
          noCustomer={noCustomer}
          customerSendBlocked={customerSendBlocked}
        />
        <AddExternalApprover
          // Flag editorNoCustomerSkipEnabled, design without a customer: the
          // e-mail dialog starts EMPTY (no address to prefill - a vendor-type
          // "customer" org's e-mail is not the recipient) and FRESH on every
          // start(entity): one hook instance serves the whole design list and
          // the dialog's state outlives start(), so without the key the field
          // would still hold the previous design's address (typed, or an
          // earlier customer's) and send this design's approval link there.
          // Flag off / with customer: no key, today's prefill, as before.
          key={noCustomer ? openCount : undefined}
          dialogVM={emailDialog}
          initEmail={noCustomer ? undefined : email}
          onDone={handleApproveWithExternal}
          onCancel={cancel}
          loading={loading}
          subject={subject}
        />
      </>
    ),
    start: (entity) => {
      setEntity(entity);
      setOpenCount((count) => count + 1);
      initApproval.openDialog();
    },
  };
}

interface AddExternalApproverProps {
  subject: string;
  dialogVM: Dialog_VM;
  loading: boolean;
  initEmail?: string;
  onDone: (email?: string) => void;
  onCancel: () => void;
}

interface InitiateApprovalDialog2_VM {
  onInitiateApproval: (e?: React.MouseEvent<HTMLElement>) => void;
  onSkipAndOrder: (e: React.MouseEvent<HTMLElement>) => void;
  onSkip: (e: React.MouseEvent<HTMLElement>) => void;
  subject: string;
  loading: boolean;
  dialogVm: Dialog_VM;
  // True = an explaining line and the "Approve without ordering" button
  // (= onSkip) in the button row, in place of the italic "Continue without
  // ordering" link; "Send Approval to Customer" and "Add to order" as today
  // (flag editorNoCustomerSkipEnabled). Omitted/false = today's dialog.
  noCustomer?: boolean;
  // B3(b): true = "Send Approval to Customer" disabled (no click, no e-mail
  // dialog) and a help line that points the dealer to the skip link below -
  // which is then a real <button> (focusable, Enter / Space) with the link's
  // own look, since it is the step the help line requires and the only
  // other keyboard-reachable way is "Add to order"; "Add to order" as
  // today. Omitted/false = today's dialog, the <p> link included.
  customerSendBlocked?: boolean;
}

function InitiateApprovalDialog2({
  onInitiateApproval,
  subject,
  onSkipAndOrder,
  onSkip,
  loading,
  dialogVm,
  noCustomer = false,
  customerSendBlocked = false,
}: InitiateApprovalDialog2_VM) {
  const { t } = useTranslation();
  const cartHidden = useCartHidden();

  function handleSkip(e: React.MouseEvent<HTMLElement>) {
    if (loading) {
      return;
    }
    onSkip(e);
  }

  // Do not show dialogs with loading spinners.
  // Loading spinners are currently reserved for the base view only to avoid duplicates.
  if (loading) return null;

  return (
    <DialogFrame dialogOption={dialogVm}>
      {loading && <LoadingElement transparent />}
      <div className={"flex flex-col gap-4 p-4"}>
        <p className="capitalize text-left text-base font-bold">{subject}</p>
        {noCustomer && (
          <p data-testid="approval-dialog-no-customer">
            {t("compDialogsInput.noCustomerChooseSendOrApprove")}
          </p>
        )}
        <p>{t("compDialogsInput.clickOptionToContinue")}</p>
        {/* Three w-48 buttons (≈ 680 px) do not fit the 400 px box on a phone
            or a tablet: the row wraps, one button per line, with the same
            16 px between them. Two buttons (today) keep today's classes. */}
        <div
          className={
            noCustomer
              ? "flex flex-wrap justify-center gap-4"
              : "flex justify-center space-x-4"
          }
        >
          <button
            className={
              "button-secondary w-48 uppercase align-items-center h-auto py-3 px-12 flex flex-col space-y-1"
            }
            onClick={customerSendBlocked ? undefined : onInitiateApproval}
            disabled={loading || customerSendBlocked}
            aria-disabled={customerSendBlocked || undefined}
          >
            <FontAwesomeIcon size={"lg"} icon={faNoteSticky} />
            <span>{t("compDialogsInput.sendApprovalToCustomer")}</span>
          </button>
          {noCustomer && (
            <button
              className={
                "button-secondary w-48 uppercase align-items-center h-auto py-3 px-12 flex flex-col space-y-1"
              }
              onClick={handleSkip}
              disabled={loading}
              data-testid="approval-dialog-approve-without-ordering"
            >
              <FontAwesomeIcon size={"lg"} icon={faCheck} />
              <span>{t("compDialogsInput.approveWithoutOrdering")}</span>
            </button>
          )}
          {!cartHidden && (
            <button
              className={
                "button-secondary w-48 uppercase align-items-center h-auto py-3 px-12 flex flex-col space-y-1"
              }
              onClick={onSkipAndOrder}
              disabled={loading}
            >
              <FontAwesomeIcon size={"lg"} icon={faCartPlus} />
              <span>{t("compDialogsInput.addToOrderAction")}</span>
            </button>
          )}
        </div>
        {customerSendBlocked && (
          <p
            className="text-sm"
            data-testid="approval-dialog-customer-send-blocked"
          >
            {t("compDialogsInput.customerSendBlockedRejectAndEdit", {
              action: cartHidden
                ? t("compDialogsInput.continueWithoutSending")
                : t("compDialogsInput.continueWithoutOrdering"),
            })}
          </p>
        )}
        {!noCustomer && customerSendBlocked && (
          <button
            type="button"
            // The <p>'s look: left (a button centres), weight 500 (the
            // global button rule is 600).
            className={
              "italic hover:underline cursor-pointer text-left font-medium"
            }
            onClick={handleSkip}
          >
            {cartHidden
              ? t("compDialogsInput.continueWithoutSending")
              : t("compDialogsInput.continueWithoutOrdering")}
          </button>
        )}
        {!noCustomer && !customerSendBlocked && (
          <p
            className={"italic hover:underline cursor-pointer"}
            onClick={handleSkip}
          >
            {cartHidden
              ? t("compDialogsInput.continueWithoutSending")
              : t("compDialogsInput.continueWithoutOrdering")}
          </p>
        )}
      </div>
    </DialogFrame>
  );
}

function AddExternalApprover({
  onDone,
  onCancel,
  loading,
  dialogVM,
  subject,
  initEmail,
}: AddExternalApproverProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>(initEmail ?? "");
  const [showError, setShowError] = useState<boolean>(false);
  const emailReg = new RegExp("^\\S+@\\S+$");

  useEffect(() => {
    if (initEmail) setEmail(initEmail);
  }, [initEmail]);

  function handleYes() {
    if (!email) {
      setShowError(true);
      return;
    }

    onDone(email);
  }

  function handleInput(input: string) {
    setShowError(!emailReg.test(input));
    setEmail(input);
  }

  if (loading) return null;

  return (
    <DialogFrame dialogOption={dialogVM}>
      {loading && <LoadingElement transparent />}
      <div className={"flex flex-col gap-4 p-4"}>
        <p className="capitalize text-left text-base font-bold">
          {t("compDialogsInput.subjectApproval", { subject })}
        </p>
        <p>{t("compDialogsInput.typeApprovalEmail")}</p>
        <InputWrapper>
          <CustomInput
            className="bg-white"
            value={email}
            onChange={(e) => handleInput(e.target.value)}
          />
          {showError && (
            <InputError
              errorMessage={t("compDialogsInput.mustBeValidEmail")}
            ></InputError>
          )}
        </InputWrapper>
        <div className={"flex justify-center space-x-4"}>
          <SecondaryButton className="flex-1" onClick={onCancel}>
            {t("cancel")}
          </SecondaryButton>
          <PrimaryButton
            className="flex-1"
            disabled={showError || loading}
            onClick={handleYes}
          >
            {t("compDialogsInput.sendApproval")}
          </PrimaryButton>
        </div>
      </div>
    </DialogFrame>
  );
}
